import { useEffect, useRef, useState } from 'react';
import { IconInfo } from '@lmig/lmds-react-icons';
import { buildSearchStatus, SearchStatusState } from '@coveo/headless';
import { useCoveoSearchEngine } from '../CoveoSearchEngine';
import { useUserData } from 'Foundation/User/UserContext';
import { IAIPortalAnswerResponse, requestAIAnswer } from 'Feature/Coveo/Helpers/AIAnswerApi';
import { renderFormattedAnswer } from './AIAnswerContent';
import { AIChat } from './AIChat';
import { CitationLinks } from './CitationLinks';
import { AIAnswerFeedback } from './AIAnswerFeedback';
import './AISummary.scss';

export function AISummary() {
  const { userData } = useUserData();
  const { headlessEngine } = useCoveoSearchEngine();
  const [aiAnswer, setAiAnswer] = useState<IAIPortalAnswerResponse | null>(null);
  const [isAnswerLoading, setIsAnswerLoading] = useState(false);
  const [answerError, setAnswerError] = useState<string | null>(null);
  const [answeredAt, setAnsweredAt] = useState<Date | null>(null);
  const [answeredQuery, setAnsweredQuery] = useState<string | null>(null);
  const lastFetchedSearchQuery = useRef<string | null>(null);
  const discardedForSearchQuery = useRef<string | null>(null);
  const latestRequestId = useRef(0);

  const fetchAIAnswer = async (query: string) => {
    const requestId = ++latestRequestId.current;
    setIsAnswerLoading(true);
    setAnswerError(null);
    setAiAnswer(null);
    setAnsweredAt(null);
    setAnsweredQuery(null);

    try {
      const parsedAnswer = await requestAIAnswer(query, () => requestId !== latestRequestId.current);
      if (!parsedAnswer) return;

      setAiAnswer(parsedAnswer);
      setAnsweredAt(new Date());
      setAnsweredQuery(query);
    } catch {
      if (requestId !== latestRequestId.current) return;
      lastFetchedSearchQuery.current = null;
      setAnswerError('We were unable to generate an AI summary for this search.');
    } finally {
      if (requestId === latestRequestId.current) {
        setIsAnswerLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!headlessEngine) return;

    const searchStatus = buildSearchStatus(headlessEngine);

    // A new search invalidates whatever answer is on screen (or still in flight) because it was
    // generated for the previous query. Dropping it as soon as the search starts - rather than when it
    // completes - means a slow response for the old query can never be rendered against the new one.
    const discardAnswerForPreviousQuery = () => {
      latestRequestId.current += 1;
      lastFetchedSearchQuery.current = null;
      setAiAnswer(null);
      setAnswerError(null);
      setAnsweredAt(null);
      setAnsweredQuery(null);
      setIsAnswerLoading(true);
    };

    const checkSearchState = (state: SearchStatusState) => {
      const requestedQuery = headlessEngine.state.query?.q?.trim();
      const executedQuery = headlessEngine.state.search.queryExecuted?.trim();

      if (state.isLoading) {
        if (
          requestedQuery !== undefined &&
          requestedQuery !== lastFetchedSearchQuery.current &&
          discardedForSearchQuery.current !== requestedQuery
        ) {
          discardedForSearchQuery.current = requestedQuery;
          discardAnswerForPreviousQuery();
        }
        return;
      }

      if (state.hasError || !state.firstSearchExecuted || !executedQuery || executedQuery === lastFetchedSearchQuery.current) {
        if (discardedForSearchQuery.current !== null) {
          // The search settled without producing a new query to summarise (for example it failed), so
          // no AI request will follow - stop showing the pending state.
          discardedForSearchQuery.current = null;
          setIsAnswerLoading(false);
        }
        return;
      }

      lastFetchedSearchQuery.current = executedQuery;
      discardedForSearchQuery.current = null;
      fetchAIAnswer(executedQuery);
    };

    checkSearchState(searchStatus.state);
    const unsub = searchStatus.subscribe(() => checkSearchState(searchStatus.state));
    return () => {
      latestRequestId.current += 1;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headlessEngine]);

  return (
    <section className="ai-summary" aria-labelledby="ai-summary-title">
      <div className="ai-summary-header">
        <h2 id="ai-summary-title">AI summary</h2>
        <p className="ai-summary-disclosure">
          <IconInfo size="16" aria-hidden="true" />
          Disclosure: This information was generated using Artificial Intelligence (AI) and may have used Google,
          Yelp, and business website sources. Review this information before using it, and please tell us if it’s
          sufficient and accurate.
        </p>
      </div>

      {/* The contents swap between idle, loading, error and answer asynchronously, so it is announced
          as a whole to assistive technology whenever it changes. */}
      <div className="ai-summary-content" role="status" aria-live="polite" aria-atomic="true">
        {isAnswerLoading && <p>Generating AI summary…</p>}
        {!isAnswerLoading && answerError && <p>{answerError}</p>}
        {!isAnswerLoading && !answerError && !aiAnswer && (
          <p>Search for something above to see an AI-generated summary.</p>
        )}
        {!isAnswerLoading && !answerError && aiAnswer && (
          <>
            {aiAnswer.answerType === 'insufficient_evidence' ? (
              <p>{aiAnswer.answer || 'We could not find enough information to answer this question.'}</p>
            ) : (
              <>
                {renderFormattedAnswer(aiAnswer.answer)}
                <CitationLinks citations={aiAnswer.citations} />
                <AIAnswerFeedback query={answeredQuery ?? ''} answer={aiAnswer.answer} />
              </>
            )}
            {aiAnswer.answerType === 'insufficient_evidence' && (
              <AIAnswerFeedback query={answeredQuery ?? ''} answer={aiAnswer.answer} />
            )}
          </>
        )}
      </div>

      {!isAnswerLoading && !answerError && aiAnswer && answeredAt && (
        <div className="ai-summary-metadata">
          <span>
            AI content, {(userData?.name?.trim() || 'system')} generated, as of {answeredAt.toLocaleDateString()}
          </span>
          {aiAnswer.confidence !== null && <span>Confidence: {Math.round(aiAnswer.confidence * 100)}%</span>}
        </div>
      )}

      {/* Follow-ups continue the summary's own AI Runtime conversation thread, so the chat only opens
          once there is a summary for it to build on. */}
      {!isAnswerLoading && !answerError && aiAnswer && answeredQuery && <AIChat topic={answeredQuery} />}

      <hr />
    </section>
  );
}

